import { DecimalSchema } from "../../decimal/decimal.schema";
import { EpochPoolUnlockSchema } from "../hold-to-earn/hold-to-earn.dto";
import z from "zod";

export const SkywalkerHteCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  tokenSymbol: z.string(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
});
export type SkywalkerHteCampaign = z.infer<typeof SkywalkerHteCampaignSchema>;

export const SkywalkerHteExchangeVolumeSchema = z.object({
  exchangeId: z.string(),
  buyQuote: DecimalSchema,
  sellQuote: DecimalSchema,
});
export type SkywalkerHteExchangeVolume = z.infer<typeof SkywalkerHteExchangeVolumeSchema>;

// Optional fields are stripped by the public route when their dashboard toggle is off.
export const SkywalkerHteEpochSchema = z.object({
  epochId: z.number().int(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  status: z.enum(["closed", "in_progress"]),
  holders: z.number().int(),
  heldTokenAmount: DecimalSchema,
  tokenPriceUsd: DecimalSchema.nullable(),
  heldUsd: DecimalSchema.nullable(),
  numberOfSpots: z.number().int().nullable().optional(),
  retainedIntoNext: z.number().int().nullable().optional(),
  volumeByExchange: z.array(SkywalkerHteExchangeVolumeSchema).optional(),
});
export type SkywalkerHteEpoch = z.infer<typeof SkywalkerHteEpochSchema>;

export const SkywalkerHteKpisSchema = z.object({
  uniqueHolders: z.number().int(),
  epochsTotal: z.number().int(),
  epochsRemaining: z.number().int(),
  hasEpochInProgress: z.boolean(),
  /** The running epoch, hidden from `epochs` because its last compute saw no price. */
  priceUnavailableEpochId: z.number().int().nullable(),
  lastEpochId: z.number().int().nullable(),
  peakHeldEpochId: z.number().int().nullable(),
  /** Pooled ratio in 0..1: sum retained / sum holders. */
  avgRetention: DecimalSchema.nullable().optional(),
  buyQuote: DecimalSchema.optional(),
  sellQuote: DecimalSchema.optional(),
  netBuyQuote: DecimalSchema.optional(),
});
export type SkywalkerHteKpis = z.infer<typeof SkywalkerHteKpisSchema>;

export const SkywalkerHteStatsSchema = z.object({
  campaign: SkywalkerHteCampaignSchema,
  kpis: SkywalkerHteKpisSchema,
  epochs: z.array(SkywalkerHteEpochSchema),
});
export type SkywalkerHteStats = z.infer<typeof SkywalkerHteStatsSchema>;

export const SkywalkerHteTopHolderSchema = z.object({
  hubUserId: z.number().int(),
  peakTokenAmount: DecimalSchema,
  totalRewardTokenAmount: DecimalSchema,
  epochsHeld: z.number().int(),
});
export type SkywalkerHteTopHolder = z.infer<typeof SkywalkerHteTopHolderSchema>;

export const SkywalkerHteInternalSchema = z.object({
  epochId: z.number().int().nullable(),
  epochsTotal: z.number().int(),
  rewardTokenSymbol: z.string(),
  poolUnlock: EpochPoolUnlockSchema.nullable(),
  topHolders: z.array(SkywalkerHteTopHolderSchema),
});
export type SkywalkerHteInternal = z.infer<typeof SkywalkerHteInternalSchema>;
